/**
 * Zod schema for `ThemePack`. Used to validate:
 *   - JSON arriving from the AI gateway in Phase 2
 *   - JSON loaded from Cloud storage in Phase 1 (tenant_themes table)
 *   - Hand-authored preset packs at module load time (caught in tests)
 *
 * The schema is intentionally permissive on string formats — we don't enforce
 * the exact "H S% L%" HSL shape because the runtime applier passes the value
 * through to `setProperty` verbatim, and CSS will gracefully ignore garbage.
 * The contrast validator (`contrast.ts`) does the heavy lifting on color
 * sanity for any theme that's about to be applied or saved.
 */
import { z } from "zod";
import { COPY_KEYS, COPY_REGISTRY, getMaxLength, isCopyKey, type CopyKey } from "@/theme/copy/registry";
import {
  COPY_PRESET_IDS,
  filterOverridesForPreset,
  isCopyPresetId,
  type CopyPresetId,
} from "@/theme/copy/presets";

const hsl = z.string().min(1, "HSL value required");

const colorTokensSchema = z.object({
  background: hsl,
  foreground: hsl,
  card: hsl,
  cardForeground: hsl,
  popover: hsl,
  popoverForeground: hsl,
  primary: hsl,
  primaryForeground: hsl,
  secondary: hsl,
  secondaryForeground: hsl,
  muted: hsl,
  mutedForeground: hsl,
  accent: hsl,
  accentForeground: hsl,
  destructive: hsl,
  destructiveForeground: hsl,
  success: hsl,
  successForeground: hsl,
  successMuted: hsl,
  successMutedForeground: hsl,
  successBorder: hsl,
  warning: hsl,
  warningForeground: hsl,
  warningMuted: hsl,
  warningMutedForeground: hsl,
  warningBorder: hsl,
  info: hsl,
  infoForeground: hsl,
  infoMuted: hsl,
  infoMutedForeground: hsl,
  infoBorder: hsl,
  border: hsl,
  input: hsl,
  ring: hsl,
  sidebarBackground: hsl,
  sidebarForeground: hsl,
  sidebarPrimary: hsl,
  sidebarPrimaryForeground: hsl,
  sidebarAccent: hsl,
  sidebarAccentForeground: hsl,
  sidebarBorder: hsl,
  sidebarRing: hsl,
  brandBackground: hsl,
  brandMain: hsl,
  brandAuxiliary1: hsl,
  brandAuxiliary2: hsl,
});

const chartTokensSchema = z.object({
  chart1: hsl,
  chart2: hsl,
  chart3: hsl,
  chart4: hsl,
  chart5: hsl,
  chart6: hsl,
  chart7: hsl,
  chart8: hsl,
  chart9: hsl,
  chart10: hsl,
  chart11: hsl,
  chart12: hsl,
});

const themeModeSchema = z.object({
  colors: colorTokensSchema,
  charts: chartTokensSchema,
});

const typographySchema = z.object({
  displayFont: z.string().min(1),
  bodyFont: z.string().min(1),
  monoFont: z.string().optional(),
  googleFontsUrl: z.string().url().optional(),
  displayLetterSpacing: z.string().optional(),
});

const surfacesSchema = z.object({
  radius: z.string().min(1),
  shadowSm: z.string().optional(),
  shadowMd: z.string().optional(),
  shadowLg: z.string().optional(),
  backdropBlur: z.string().optional(),
});

const assetsSchema = z
  .object({
    logoUrl: z.string().optional(),
    logoUrlDark: z.string().optional(),
    faviconUrl: z.string().optional(),
    loginBackgroundUrl: z.string().optional(),
  })
  .optional();

/**
 * Strict label override schema.
 *
 * - Keys MUST exist in the COPY_REGISTRY (unknown keys are dropped, not just
 *   ignored, with a console warning so theme authors notice).
 * - Values are length-capped per-key (default 32 chars) to prevent layout
 *   breakage on buttons and nav items.
 * - Destructive keys are intentionally absent from the registry, so a theme
 *   that tries to override them will fail validation here.
 */
const labelsSchema = z
  .record(z.string(), z.string())
  .optional()
  .transform((raw, ctx) => {
    if (!raw) return undefined;
    const cleaned: Partial<Record<CopyKey, string>> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (!isCopyKey(key)) {
        // Drop unknown keys but warn — themes evolve faster than the registry.
        // eslint-disable-next-line no-console
        console.warn(`[theme] dropping unknown label key "${key}"`);
        continue;
      }
      const max = getMaxLength(key);
      if (value.length > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["labels", key],
          message: `label override for "${key}" exceeds ${max} chars`,
        });
        continue;
      }
      if (!value.trim()) continue;
      cleaned[key] = value;
    }
    return cleaned;
  });

// Re-export for prompt builders / docs generators.
export { COPY_KEYS, COPY_REGISTRY };

const metaSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, "slug must be lowercase alphanumeric with hyphens"),
  name: z.string().min(1),
  description: z.string().optional(),
  author: z.string().optional(),
  version: z.string().min(1),
  supportedModes: z.enum(["light", "dark", "both"]),
  copyPreset: z
    .enum(COPY_PRESET_IDS as [CopyPresetId, ...CopyPresetId[]])
    .optional(),
});

export const themePackSchema = z
  .object({
    meta: metaSchema,
    light: themeModeSchema.optional(),
    dark: themeModeSchema.optional(),
    typography: typographySchema,
    surfaces: surfacesSchema,
    assets: assetsSchema,
    labels: labelsSchema,
  })
  .superRefine((pack, ctx) => {
    // Cross-field rule: `supportedModes` must match presence of `light`/`dark`.
    const { supportedModes } = pack.meta;
    if ((supportedModes === "light" || supportedModes === "both") && !pack.light) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["light"],
        message: "light mode tokens are required when supportedModes includes light",
      });
    }
    if ((supportedModes === "dark" || supportedModes === "both") && !pack.dark) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dark"],
        message: "dark mode tokens are required when supportedModes includes dark",
      });
    }
  })
  .transform((pack) => {
    // Apply the theme's declared copy preset (if any) to its labels. This
    // strips any overrides that fall outside the preset's allowlist *before*
    // the runtime ever sees them — defense in depth on top of the per-key
    // registry check above.
    if (pack.labels && pack.meta.copyPreset) {
      const { allowed } = filterOverridesForPreset(
        pack.labels,
        pack.meta.copyPreset,
      );
      const droppedKeys = Object.keys(pack.labels).filter(
        (k) => !(k in allowed),
      );
      if (droppedKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `[theme] copy preset "${pack.meta.copyPreset}" disallowed ${droppedKeys.length} label(s): ${droppedKeys.join(", ")}`,
        );
      }
      pack.labels = allowed;
    }
    return pack;
  });

export type ThemePackParsed = z.infer<typeof themePackSchema>;

// Re-export preset utilities so consumers don't need a second import path.
export { filterOverridesForPreset, isCopyPresetId };
export type { CopyPresetId };
